// Port of Services/SBotChatCloudSyncService.swift (Firestore + Storage sync).
//
// Web adaptation: iOS keeps a local JSON store and loads every conversation's
// messages inside the listener. On the web, Firestore is the source of truth:
// the listener parses conversation summaries only (for the recents drawer) and
// messages are loaded when a conversation is opened. Collections, document
// shapes, storage paths, soft-delete semantics, and merge-by-updatedAt all
// match the iOS mapper so both clients share history.

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  writeBatch,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { getBytes, ref, uploadString, type FirebaseStorage } from "firebase/storage";
import { db, storage } from "@/firebase/app";
import {
  attachmentStoragePath,
  conversationFirestoreData,
  messageFirestoreData,
  parseChatConversation,
  parseChatMessage,
  type SBotChatAttachment,
  type SBotChatConversation,
  type SBotChatMessage,
  type SBotChatWorkspaceScope,
} from "@/models/aiChat";

/** Mirrors SBotChatController.maxAttachmentBytes (15 MB). */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const SAVE_DEBOUNCE_MS = 350;

function chatsCollection(firestore: Firestore, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): CollectionReference {
  if (workspaceScope) {
    return collection(firestore, "users", userID, "aiChatWorkspaces", workspaceScope.storageKey, "chats");
  }
  return collection(firestore, "users", userID, "aiChats");
}

export class SBotChatSyncService {
  private readonly firestore: Firestore;
  private readonly storageClient: FirebaseStorage;
  private readonly pendingSaves = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly savesInFlight = new Map<string, Promise<void>>();

  constructor(firestore: Firestore = db, storageClient: FirebaseStorage = storage) {
    this.firestore = firestore;
    this.storageClient = storageClient;
  }

  /**
   * Real-time listener over the workspace's conversations (summaries only —
   * message subcollections load on open). Soft-deleted docs are filtered.
   */
  listenConversationSummaries(
    userID: string,
    workspaceScope: SBotChatWorkspaceScope | undefined,
    onChange: (conversations: SBotChatConversation[]) => void,
  ): Unsubscribe {
    const chatsQuery = query(chatsCollection(this.firestore, userID, workspaceScope), orderBy("updatedAt", "desc"));
    return onSnapshot(chatsQuery, (snapshot) => {
      const conversations = snapshot.docs
        .map((document) => parseChatConversation(document.id, document.data(), []))
        .filter((value): value is SBotChatConversation => value !== undefined);
      onChange(conversations);
    });
  }

  /** Loads one conversation with its full message history (lazy attachment download). */
  async loadConversationMessages(
    userID: string,
    workspaceScope: SBotChatWorkspaceScope | undefined,
    conversationID: string,
  ): Promise<SBotChatMessage[]> {
    const messagesQuery = query(
      collection(chatsCollection(this.firestore, userID, workspaceScope), conversationID, "messages"),
      orderBy("createdAt"),
    );
    const snapshot = await getDocs(messagesQuery);
    const messages: SBotChatMessage[] = [];
    for (const document of snapshot.docs as QueryDocumentSnapshot<DocumentData>[]) {
      const message = await parseChatMessage(document.id, document.data(), (storagePath, byteCount) =>
        this.downloadAttachmentBase64(storagePath, byteCount),
      );
      if (message) messages.push(message);
    }
    return messages;
  }

  /** Debounced save (350 ms per conversation, mirroring iOS). Call flushPendingSave when streaming ends. */
  saveConversationDebounced(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): void {
    const existing = this.pendingSaves.get(conversation.id);
    if (existing) clearTimeout(existing);
    this.pendingSaves.set(
      conversation.id,
      setTimeout(() => {
        this.pendingSaves.delete(conversation.id);
        void this.saveConversation(conversation, userID, workspaceScope).catch((error: unknown) => {
          console.warn("SBotChatSyncService save failed:", error);
        });
      }, SAVE_DEBOUNCE_MS),
    );
  }

  async saveConversation(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): Promise<void> {
    const key = `${userID}/${workspaceScope?.storageKey ?? "personal"}/${conversation.id}`;
    const previous = this.savesInFlight.get(key);
    const save = (previous ?? Promise.resolve()).catch(() => undefined)
      .then(() => this.saveConversationNow(conversation, userID, workspaceScope));
    this.savesInFlight.set(key, save);
    try {
      await save;
    } finally {
      if (this.savesInFlight.get(key) === save) this.savesInFlight.delete(key);
    }
  }

  private async saveConversationNow(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): Promise<void> {
    if (conversation.messages.length === 0) return;

    const scoped: SBotChatConversation = { ...conversation, workspaceScope };
    const conversationRef = doc(chatsCollection(this.firestore, userID, workspaceScope), scoped.id);

    // Attachments upload before the metadata that references them.
    for (const message of scoped.messages) {
      await this.uploadAttachments(message.attachments, userID, workspaceScope, scoped.id, message.id);
    }

    const batch = writeBatch(this.firestore);
    batch.set(conversationRef, conversationFirestoreData(scoped));
    for (const message of scoped.messages) {
      batch.set(
        doc(collection(conversationRef, "messages"), message.id),
        messageFirestoreData(message, { userID, conversationID: scoped.id, workspaceScope }),
      );
    }
    await batch.commit();

    await this.softDeleteMessagesMissingFrom(scoped, userID, workspaceScope);
  }

  /** Soft delete: rules forbid hard deletes; docs keep a deletedAt marker. */
  async softDeleteConversation(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): Promise<void> {
    const pending = this.pendingSaves.get(conversation.id);
    if (pending) {
      clearTimeout(pending);
      this.pendingSaves.delete(conversation.id);
    }
    const conversationRef = doc(chatsCollection(this.firestore, userID, workspaceScope), conversation.id);
    const batch = writeBatch(this.firestore);
    batch.set(conversationRef, conversationFirestoreData({ ...conversation, workspaceScope }, new Date()), { merge: true });
    batch.commit().catch((error: unknown) => console.warn("SBotChatSyncService delete failed:", error));
  }

  flushPendingSave(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): void {
    void this.flushPendingSaveAsync(conversation, userID, workspaceScope).catch((error: unknown) => {
      console.warn("SBotChatSyncService flush failed:", error);
    });
  }

  /** Approval execution must be saved before a model continuation can begin. */
  async flushPendingSaveAsync(conversation: SBotChatConversation, userID: string, workspaceScope: SBotChatWorkspaceScope | undefined): Promise<void> {
    const pending = this.pendingSaves.get(conversation.id);
    if (pending) {
      clearTimeout(pending);
      this.pendingSaves.delete(conversation.id);
    }
    await this.saveConversation(conversation, userID, workspaceScope);
  }

  private async uploadAttachments(
    attachments: SBotChatAttachment[],
    userID: string,
    workspaceScope: SBotChatWorkspaceScope | undefined,
    conversationID: string,
    messageID: string,
  ): Promise<void> {
    for (const attachment of attachments) {
      if (attachment.base64Data.length === 0) continue;
      const storagePath = attachmentStoragePath(userID, workspaceScope, conversationID, messageID, attachment.id);
      const reference = ref(this.storageClient, storagePath);
      await uploadString(reference, attachment.base64Data, "base64", { contentType: attachment.mimeType });
    }
  }

  private async softDeleteMessagesMissingFrom(
    conversation: SBotChatConversation,
    userID: string,
    workspaceScope: SBotChatWorkspaceScope | undefined,
  ): Promise<void> {
    const messageIDs = new Set(conversation.messages.map((message) => message.id));
    const conversationRef = doc(chatsCollection(this.firestore, userID, workspaceScope), conversation.id);
    const snapshot = await getDocs(collection(conversationRef, "messages"));
    const missing = snapshot.docs.filter((document) => !messageIDs.has(document.id));
    if (missing.length === 0) return;

    const batch = writeBatch(this.firestore);
    const deletedAt = new Date();
    for (const document of missing) {
      batch.set(document.ref, { deletedAt }, { merge: true });
    }
    await batch.commit();
  }

  /**
   * Returns "" when the object cannot be read (offline, CORS, rules, deleted object)
   * so one unreadable attachment degrades to a missing image instead of rejecting
   * the whole conversation load. Safe to persist: uploadAttachments skips empty
   * base64Data, so a later save never overwrites the stored object with nothing.
   */
  private async downloadAttachmentBase64(storagePath: string, byteCount: number): Promise<string> {
    const reference = ref(this.storageClient, storagePath);
    const maxSize = Math.max(byteCount, MAX_ATTACHMENT_BYTES);
    try {
      const buffer = await getBytes(reference, maxSize);
      return arrayBufferToBase64(buffer);
    } catch (error: unknown) {
      console.warn(`SBotChatSyncService attachment download failed (${storagePath}):`, error);
      return "";
    }
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}
